import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, InputAdornment,
  IconButton, Avatar, Chip, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Skeleton,
  useTheme, alpha, Tabs, Tab, FormControl, InputLabel, Select,
  MenuItem, Divider, Tooltip, Collapse,
} from '@mui/material';
import {
  Search, Refresh, ViewList, ViewModule,
  ArrowUpward, ArrowDownward, FilterList, Close,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { teamsApi } from '../services/apiService';
import { themeUtils } from '../theme/theme';
import { getCVRDisplay } from '../utils/cvrCalculations';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import useMobile from '../hooks/useMobile';

/* ═══════════════════════════════════════════════════════════════════════════
   Constants & Helpers — mirrors Players.js patterns
   ═══════════════════════════════════════════════════════════════════════════ */

const TEAM_FULL_NAMES = {
  ARI:'Arizona', AZ:'Arizona', ATL:'Atlanta', BAL:'Baltimore', BOS:'Boston',
  CHC:'Chi Cubs', CWS:'Chi Sox', CHW:'Chi Sox', CIN:'Cincinnati', CLE:'Cleveland',
  COL:'Colorado', DET:'Detroit', HOU:'Houston', KC:'Kansas City', KCR:'Kansas City',
  LAA:'LA Angels', LAD:'LA Dodgers', MIA:'Miami', MIL:'Milwaukee', MIN:'Minnesota',
  NYM:'NY Mets', NYY:'NY Yankees', OAK:'Oakland', ATH:'Oakland', PHI:'Philadelphia',
  PIT:'Pittsburgh', SD:'San Diego', SDP:'San Diego', SF:'San Francisco', SFG:'San Francisco',
  SEA:'Seattle', STL:'St. Louis', TB:'Tampa Bay', TBR:'Tampa Bay', TEX:'Texas',
  TOR:'Toronto', WSH:'Washington', WSN:'Washington',
};

const teamLogo = (id) => getSharedTeamLogoUrl(id, 500);
const getNested = (o, p) => p.split('.').reduce((c, k) => c?.[k], o);

const fmtMoney = (v) => {
  if (v == null || v === 0 || isNaN(Number(v))) return '\u2014';
  v = Number(v);
  if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return '$' + Math.round(v / 1e3) + 'K';
  return '$' + Math.round(v).toLocaleString();
};
const fmtDec = (d) => (v) => (v == null || isNaN(Number(v))) ? '\u2014' : Number(v).toFixed(d);
const fmtAvg = fmtDec(3);
const fmtInt = (v) => (v == null || isNaN(Number(v))) ? '\u2014' : Math.round(Number(v)).toString();
const fmtPct = (v) => (v == null || isNaN(Number(v))) ? '\u2014' : (Number(v) * 100).toFixed(1) + '%';
const fmtPct100 = (v) => (v == null || isNaN(Number(v))) ? '\u2014' : Number(v).toFixed(1) + '%';
const fmtStr = (v) => (v == null) ? '\u2014' : String(v);

// Savant-style heat-map: subtle bg tint + fg color based on rank in pool (same as Players.js)
const buildHeatMap = (rows, key, invert = false) => {
  const vals = rows.map(r => {
    const v = getNested(r, key);
    return (v == null) ? null : parseFloat(v);
  }).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return {};
  const sorted = [...vals].sort((a, b) => a - b);
  const pctOf = (v) => { const idx = sorted.findIndex(x => x >= v); return idx < 0 ? 1 : idx / (sorted.length - 1); };
  const out = {};
  for (const r of rows) {
    const raw = getNested(r, key);
    const v = (raw == null) ? NaN : parseFloat(raw);
    if (isNaN(v)) { out[r.id] = null; continue; }
    const pc = invert ? 1 - pctOf(v) : pctOf(v);
    let bg, fg;
    if      (pc >= 0.80) { bg = 'rgba(0,200,83,0.17)';   fg = '#00c853'; }
    else if (pc >= 0.60) { bg = 'rgba(124,179,66,0.12)'; fg = '#7cb342'; }
    else if (pc >= 0.40) { bg = 'transparent';           fg = 'inherit'; }
    else if (pc >= 0.20) { bg = 'rgba(239,108,0,0.11)';  fg = '#ef6c00'; }
    else                 { bg = 'rgba(198,40,40,0.14)';  fg = '#c62828'; }
    out[r.id] = { bg, fg };
  }
  return out;
};

// Stats where lower is better (invert heat-map + default sort asc)
const INVERT_STATS = new Set([
  'stats.pitching.era', 'stats.pitching.whip', 'stats.pitching.fip',
  'stats.pitching.walksPer9Inn', 'stats.pitching.hitsPer9Inn', 'stats.pitching.homeRunsPer9',
  'stats.pitching.runs', 'stats.pitching.earnedRuns', 'stats.pitching.homeRuns',
  'stats.pitching.baseOnBalls', 'stats.pitching.blownSaves', 'stats.pitching.losses',
  'stats.batting.kRate', 'stats.batting.strikeOuts',
  'totalPayroll', 'dollarsPerWin', 'dollarsPerWAR',
]);

/* ── Stat catalogs ────────────────────────────────────────────────────── */

const BATTING_STATS = [
  { key: 'stats.batting.avg',   label: 'AVG',  format: fmtAvg },
  { key: 'stats.batting.obp',   label: 'OBP',  format: fmtAvg },
  { key: 'stats.batting.slg',   label: 'SLG',  format: fmtAvg },
  { key: 'stats.batting.ops',   label: 'OPS',  format: fmtAvg },
  { key: 'stats.batting.wOBA',  label: 'wOBA', format: fmtAvg },
  { key: 'stats.batting.iso',   label: 'ISO',  format: fmtAvg },
  { key: 'stats.batting.homeRuns', label: 'HR', format: fmtInt },
  { key: 'stats.batting.runs',  label: 'R',    format: fmtInt },
  // power
  { key: 'stats.batting.extraBaseHits',    label: 'XBH',   format: fmtInt },
  { key: 'stats.batting.atBatsPerHomeRun', label: 'AB/HR', format: fmtDec(1) },
  // discipline
  { key: 'stats.batting.kRate',       label: 'K%',    format: fmtPct },
  { key: 'stats.batting.bbRate',      label: 'BB%',   format: fmtPct },
  { key: 'stats.batting.contactRate', label: 'CT%',   format: fmtPct },
  { key: 'stats.batting.babip',       label: 'BABIP', format: fmtAvg },
  // counting
  { key: 'stats.batting.hits',        label: 'H',   format: fmtInt },
  { key: 'stats.batting.rbi',         label: 'RBI', format: fmtInt },
  { key: 'stats.batting.doubles',     label: '2B',  format: fmtInt },
  { key: 'stats.batting.triples',     label: '3B',  format: fmtInt },
  { key: 'stats.batting.stolenBases', label: 'SB',  format: fmtInt },
  { key: 'stats.batting.baseOnBalls', label: 'BB',  format: fmtInt },
  { key: 'stats.batting.strikeOuts',  label: 'K',   format: fmtInt },
];

const PITCHING_STATS = [
  { key: 'stats.pitching.era',                label: 'ERA',  format: fmtDec(2) },
  { key: 'stats.pitching.whip',               label: 'WHIP', format: fmtDec(2) },
  { key: 'stats.pitching.fip',                label: 'FIP',  format: fmtDec(2) },
  { key: 'stats.pitching.strikeoutsPer9Inn',  label: 'K/9',  format: fmtDec(1) },
  { key: 'stats.pitching.walksPer9Inn',       label: 'BB/9', format: fmtDec(1) },
  { key: 'stats.pitching.homeRunsPer9',       label: 'HR/9', format: fmtDec(1) },
  { key: 'stats.pitching.strikeoutWalkRatio', label: 'K/BB', format: fmtDec(1) },
  { key: 'stats.pitching.qualityStart',       label: 'QS',   format: fmtInt },
  // bullpen
  { key: 'stats.pitching.saves',          label: 'SV',  format: fmtInt },
  { key: 'stats.pitching.savePercentage', label: 'SV%', format: fmtPct100 },
  { key: 'stats.pitching.holds',          label: 'HLD', format: fmtInt },
  { key: 'stats.pitching.blownSaves',     label: 'BS',  format: fmtInt },
  // efficiency
  { key: 'stats.pitching.strikePercentage',     label: 'STRK%', format: fmtPct100 },
  { key: 'stats.pitching.leftOnBasePercentage', label: 'LOB%',  format: fmtPct100 },
  { key: 'stats.pitching.hitsPer9Inn',          label: 'H/9',   format: fmtDec(1) },
  // counting
  { key: 'stats.pitching.strikeOuts',     label: 'K',   format: fmtInt },
  { key: 'stats.pitching.baseOnBalls',    label: 'BB',  format: fmtInt },
  { key: 'stats.pitching.earnedRuns',     label: 'ER',  format: fmtInt },
  { key: 'stats.pitching.runs',           label: 'RA',  format: fmtInt },
  { key: 'stats.pitching.homeRuns',       label: 'HR',  format: fmtInt },
  { key: 'stats.pitching.inningsPitched', label: 'IP',  format: fmtStr },
];

// Value stats available under both tabs via the "value" group
const VALUE_STATS = [
  { key: 'totalPayroll',  label: 'Payroll', format: fmtMoney },
  { key: 'dollarsPerWin', label: '$/Win',   format: fmtMoney },
  { key: 'dollarsPerWAR', label: '$/WAR',   format: fmtMoney },
];

const buildStatGroups = (category) => {
  const stats = category === 'batting' ? BATTING_STATS : PITCHING_STATS;
  const pick = (keys) => stats.filter(s => keys.includes(s.key));
  if (category === 'batting') {
    return {
      primary: stats.slice(0, 8),
      power: pick(['stats.batting.homeRuns', 'stats.batting.iso', 'stats.batting.slg', 'stats.batting.extraBaseHits', 'stats.batting.atBatsPerHomeRun']),
      discipline: pick(['stats.batting.kRate', 'stats.batting.bbRate', 'stats.batting.contactRate', 'stats.batting.babip']),
      counting: pick(['stats.batting.runs', 'stats.batting.hits', 'stats.batting.homeRuns', 'stats.batting.rbi', 'stats.batting.doubles', 'stats.batting.triples', 'stats.batting.stolenBases', 'stats.batting.baseOnBalls', 'stats.batting.strikeOuts']),
      value: VALUE_STATS,
    };
  }
  return {
    primary: stats.slice(0, 8),
    bullpen: pick(['stats.pitching.saves', 'stats.pitching.savePercentage', 'stats.pitching.holds', 'stats.pitching.blownSaves']),
    efficiency: pick(['stats.pitching.strikePercentage', 'stats.pitching.leftOnBasePercentage', 'stats.pitching.hitsPer9Inn', 'stats.pitching.qualityStart']),
    counting: pick(['stats.pitching.strikeOuts', 'stats.pitching.baseOnBalls', 'stats.pitching.earnedRuns', 'stats.pitching.runs', 'stats.pitching.homeRuns', 'stats.pitching.inningsPitched']),
    value: VALUE_STATS,
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

const Teams = () => {
  const { year } = useYear();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();

  const [teams, setTeams]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [searchTerm, setSearchTerm]         = useState('');
  const [activeCategory, setActiveCategory] = useState('batting');
  const [sortBy, setSortBy]                 = useState('winPct');
  const [sortOrder, setSortOrder]           = useState('desc');
  const [selectedLeague, setSelectedLeague]     = useState('all');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedStatGroup, setSelectedStatGroup]     = useState('primary');
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => { if (isMobile) setViewMode('cards'); }, [isMobile]);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await teamsApi.getTeams({ year, mode: 'rolling', limit: 100, category: 'all' });
      const raw = res.teams || [];
      const enriched = raw.map(t => {
        const wins   = t.record?.wins   || 0;
        const losses = t.record?.losses || 0;
        const gp = wins + losses;
        const warTotal = Number(t.war?.total || 0);
        const payroll = Number(t.totalPayroll || 0);
        return {
          ...t,
          fullName: TEAM_FULL_NAMES[t.id] || t.name || t.id,
          winPct: gp > 0 ? wins / gp : 0,
          warTotal,
          runDiff: Number(t.standings?.runDifferential || 0),
          dollarsPerWin: (payroll > 0 && wins > 0) ? payroll / wins : null,
          dollarsPerWAR: (payroll > 0 && warTotal > 0.5) ? payroll / warTotal : null,
        };
      });
      setTeams(enriched);
    } catch (err) { console.error('Error loading teams:', err); setError(err.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const currentStats = activeCategory === 'batting' ? BATTING_STATS : PITCHING_STATS;
  const statGroups   = useMemo(() => buildStatGroups(activeCategory), [activeCategory]);

  const divisions = useMemo(() => [...new Set(teams.map(t => t.division).filter(Boolean))].sort(), [teams]);

  /* ── Filter + sort (client-side, 30 rows) ── */
  const filteredTeams = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = teams;
    if (q) list = list.filter(t =>
      t.id.toLowerCase().includes(q) ||
      (t.fullName || '').toLowerCase().includes(q) ||
      (t.name || '').toLowerCase().includes(q));
    if (selectedLeague !== 'all') list = list.filter(t => (t.league || '').includes(selectedLeague));
    if (selectedDivision !== 'all') list = list.filter(t => t.division === selectedDivision);
    return list;
  }, [teams, searchTerm, selectedLeague, selectedDivision]);

  const sortedTeams = useMemo(() => {
    const arr = [...filteredTeams];
    arr.sort((a, b) => {
      const aRaw = getNested(a, sortBy); const bRaw = getNested(b, sortBy);
      const aV = (aRaw == null) ? NaN : parseFloat(aRaw);
      const bV = (bRaw == null) ? NaN : parseFloat(bRaw);
      if (isNaN(aV) && isNaN(bV)) return 0;
      if (isNaN(aV)) return 1;
      if (isNaN(bV)) return -1;
      return sortOrder === 'desc' ? bV - aV : aV - bV;
    });
    return arr;
  }, [filteredTeams, sortBy, sortOrder]);

  const handleSort = useCallback((field) => {
    setSortBy(prev => {
      if (field === prev) { setSortOrder(o => o === 'desc' ? 'asc' : 'desc'); return prev; }
      setSortOrder(INVERT_STATS.has(field) ? 'asc' : 'desc');
      return field;
    });
  }, []);

  const goToTeam = useCallback((t) => navigate(`/teams/${t.id}/${year}`, { state: { prefill: t } }), [navigate, year]);

  if (loading && !teams.length) return <TeamsSkeleton />;
  if (error) return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography color="error" variant="h6">Something went wrong. Please try again.</Typography>
      <Button onClick={loadTeams} sx={{ mt: 2 }}>Retry</Button>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1600, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>

      {/* ════════ Row 1: search + year ════════ */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search teams…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200,
            '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: alpha(theme.palette.background.paper, 0.6) } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: 'text.disabled' }} /></InputAdornment>,
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Close sx={{ fontSize: 16 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }} />
        <PageYearSelector />
      </Box>

      {/* ════════ Row 2: tabs + sort + view controls ════════ */}
      <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tabs value={activeCategory}
            onChange={(_, v) => {
              setActiveCategory(v);
              setSelectedStatGroup('primary');
              if (sortBy.startsWith('stats.')) { setSortBy('winPct'); setSortOrder('desc'); }
            }}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, px: 1.5, fontSize: '0.8rem', fontWeight: 700, textTransform: 'none' } }}>
            <Tab label="Batting" value="batting" />
            <Tab label="Pitching" value="pitching" />
          </Tabs>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select value={sortBy} onChange={e => handleSort(e.target.value)}
              displayEmpty
              sx={{ fontSize: '0.75rem', fontWeight: 600, '& .MuiSelect-select': { py: 0.4 } }}>
              <MenuItem value="winPct">Win %</MenuItem>
              <MenuItem value="runDiff">Run Diff</MenuItem>
              <MenuItem value="warTotal">WAR</MenuItem>
              <MenuItem value="cvr">CVR</MenuItem>
              <MenuItem value="acvr">ACVR</MenuItem>
              <Divider />
              {currentStats.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>

          <Tooltip title={`Sort ${sortOrder === 'desc' ? 'Descending' : 'Ascending'}`}>
            <IconButton size="small" onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')} sx={{ p: 0.5 }}>
              {sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: 18 }} /> : <ArrowUpward sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
            <Tooltip title="Table View"><IconButton size="small" onClick={() => setViewMode('table')} color={viewMode === 'table' ? 'primary' : 'default'} sx={{ p: 0.5 }}><ViewList sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title="Card View"><IconButton size="small" onClick={() => setViewMode('cards')} color={viewMode === 'cards' ? 'primary' : 'default'} sx={{ p: 0.5 }}><ViewModule sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title="Refresh"><IconButton size="small" onClick={loadTeams} sx={{ p: 0.5 }}><Refresh sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title={showAdvancedFilters ? 'Hide Filters' : 'Filters'}>
              <IconButton size="small" onClick={() => setShowAdvancedFilters(f => !f)} color={showAdvancedFilters ? 'primary' : 'default'} sx={{ p: 0.5 }}><FilterList sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Collapsible filters */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>League</InputLabel>
              <Select value={selectedLeague} onChange={e => setSelectedLeague(e.target.value)} label="League" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="American">AL</MenuItem>
                <MenuItem value="National">NL</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Division</InputLabel>
              <Select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)} label="Division" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="all">All</MenuItem>
                {divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            {(selectedLeague !== 'all' || selectedDivision !== 'all') && (
              <Button size="small" variant="text" color="error" sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                onClick={() => { setSelectedLeague('all'); setSelectedDivision('all'); }}>
                Clear
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
              {sortedTeams.length} teams
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* ════════ Main Data Display ════════ */}
      <AnimatePresence mode="wait">
        {viewMode === 'table' ? (
          <motion.div key="table" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            <TeamsTable
              teams={sortedTeams} stats={currentStats}
              sortBy={sortBy} sortOrder={sortOrder}
              selectedStatGroup={selectedStatGroup} onStatGroupChange={setSelectedStatGroup}
              statGroups={statGroups}
              isMobile={isMobile}
              onSort={handleSort}
              onTeamClick={goToTeam}
            />
          </motion.div>
        ) : (
          <motion.div key="cards" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            <TeamsGrid
              teams={sortedTeams}
              activeCategory={activeCategory}
              isMobile={isMobile}
              onTeamClick={goToTeam}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TeamsTable — W-L, Diff, WAR, CVR, ACVR as fixed prominent columns
   ═══════════════════════════════════════════════════════════════════════════ */

const TeamsTable = ({ teams, stats, sortBy, sortOrder, selectedStatGroup, onStatGroupChange, statGroups, isMobile, onSort, onTeamClick }) => {
  const theme = useTheme();

  const visibleStats = selectedStatGroup === 'all' ? stats : (statGroups[selectedStatGroup] || stats.slice(0, 8));

  const SortHeader = ({ field, label, sx: sxProp = {} }) => {
    const active = sortBy === field;
    return (
      <TableCell align="center" sx={{ minWidth: { xs: 56, sm: 70 }, cursor: 'pointer', userSelect: 'none',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
        ...(active && { bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }),
        ...sxProp }}
        onClick={() => onSort(field)}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>{label}</Typography>
          {active && (sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: '0.8rem' }} /> : <ArrowUpward sx={{ fontSize: '0.8rem' }} />)}
        </Box>
      </TableCell>
    );
  };

  const heatMaps = useMemo(() => {
    const keysToMap = [
      { key: 'winPct',   invert: false },
      { key: 'runDiff',  invert: false },
      { key: 'warTotal', invert: false },
      { key: 'cvr',      invert: false },
      { key: 'acvr',     invert: false },
      ...visibleStats.map(s => ({ key: s.key, invert: INVERT_STATS.has(s.key) })),
    ];
    const out = {};
    for (const { key, invert } of keysToMap) out[key] = buildHeatMap(teams, key, invert);
    return out;
  }, [teams, visibleStats]);

  return (
    <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* Stat-group chips */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: 'flex', gap: 0.5, flexWrap: { xs: 'nowrap', sm: 'wrap' }, alignItems: 'center', overflowX: { xs: 'auto', sm: 'visible' }, WebkitOverflowScrolling: 'touch', pb: { xs: 2, sm: 1.5 }, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'rgba(128,128,128,0.3)' } }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 1 }}>Stats:</Typography>
        {Object.keys(statGroups).map(gk => (
          <Chip key={gk} label={gk.charAt(0).toUpperCase() + gk.slice(1)} size="small"
            variant={selectedStatGroup === gk ? 'filled' : 'outlined'}
            color={selectedStatGroup === gk ? 'primary' : 'default'}
            onClick={() => onStatGroupChange(gk)}
            sx={{ cursor: 'pointer', textTransform: 'capitalize', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }} />
        ))}
        <Chip label="All" size="small"
          variant={selectedStatGroup === 'all' ? 'filled' : 'outlined'}
          color={selectedStatGroup === 'all' ? 'primary' : 'default'}
          onClick={() => onStatGroupChange('all')}
          sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }} />
      </Box>

      <TableContainer sx={{ maxHeight: '75vh', overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{
                position: 'sticky', left: 0, zIndex: 1100, width: { xs: 32, sm: 40 }, pl: { xs: 1, sm: 2 },
                fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', textAlign: 'center',
                bgcolor: alpha(theme.palette.background.paper, 0.97),
              }}>#</TableCell>
              <TableCell sx={{
                position: 'sticky', left: { xs: 32, sm: 40 }, zIndex: 1100, minWidth: { xs: 110, sm: 180 },
                fontWeight: 700, bgcolor: alpha(theme.palette.background.paper, 0.97),
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}>Team</TableCell>
              <SortHeader field="winPct" label="W-L" />
              <SortHeader field="runDiff" label="DIFF" />
              <SortHeader field="warTotal" label="WAR" />
              {!isMobile && <SortHeader field="cvr" label="CVR" />}
              {!isMobile && <SortHeader field="acvr" label="ACVR" />}
              {visibleStats.map(s => <SortHeader key={s.key} field={s.key} label={s.label} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((t, idx) => {
              const winHeat  = heatMaps['winPct']?.[t.id];
              const diffHeat = heatMaps['runDiff']?.[t.id];
              const warHeat  = heatMaps['warTotal']?.[t.id];
              const cvrHeat  = heatMaps['cvr']?.[t.id];
              const acvrHeat = heatMaps['acvr']?.[t.id];
              const wins = t.record?.wins || 0;
              const losses = t.record?.losses || 0;

              return (
                <TableRow key={t.id} hover onClick={() => onTeamClick(t)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                  {/* Rank */}
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1000, textAlign: 'center', px: 0.5, pl: { xs: 1, sm: 2 } }}>
                    <Typography variant="body2" color="text.disabled" fontWeight={700} sx={{ fontSize: '0.72rem' }}>{idx + 1}</Typography>
                  </TableCell>

                  {/* Team identity */}
                  <TableCell sx={{
                    position: 'sticky', left: { xs: 32, sm: 40 }, bgcolor: 'background.paper', zIndex: 1000,
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar src={teamLogo(t.id)}
                        sx={{ width: 28, height: 28, bgcolor: 'white', flexShrink: 0 }}
                        imgProps={{ style: { objectFit: 'contain' } }}>{t.id}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                          {isMobile ? t.id : t.fullName}
                        </Typography>
                        {!isMobile && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                            {(t.division || '').replace('American League', 'AL').replace('National League', 'NL')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* W-L */}
                  <TableCell align="center" sx={{ px: 0.75, bgcolor: winHeat?.bg || 'transparent' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, color: winHeat?.fg || 'text.primary', whiteSpace: 'nowrap' }}>
                      {wins}-{losses}
                    </Typography>
                  </TableCell>

                  {/* Run Diff */}
                  <TableCell align="center" sx={{ px: 0.75, bgcolor: diffHeat?.bg || 'transparent' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: diffHeat?.fg || 'text.primary' }}>
                      {t.runDiff > 0 ? '+' : ''}{t.runDiff}
                    </Typography>
                  </TableCell>

                  {/* WAR */}
                  <TableCell align="center" sx={{ px: 0.75, bgcolor: warHeat?.bg || 'transparent' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: warHeat?.fg || 'text.primary' }}>
                      {t.warTotal.toFixed(1)}
                    </Typography>
                  </TableCell>

                  {/* CVR */}
                  {!isMobile && (
                    <TableCell align="center" sx={{ px: 0.75, bgcolor: cvrHeat?.bg || 'transparent' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: cvrHeat?.fg || 'text.primary' }}>
                        {t.cvr != null ? Number(t.cvr).toFixed(0) : '\u2014'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* ACVR */}
                  {!isMobile && (
                    <TableCell align="center" sx={{ px: 0.75, bgcolor: acvrHeat?.bg || 'transparent' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: acvrHeat?.fg || 'text.primary' }}>
                        {t.acvr != null ? Number(t.acvr).toFixed(1) : '\u2014'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Per-stat cells — heat-tinted */}
                  {visibleStats.map(stat => {
                    const heat = heatMaps[stat.key]?.[t.id];
                    const val  = getNested(t, stat.key);
                    return (
                      <TableCell key={stat.key} align="center" sx={{ px: 0.75, bgcolor: heat?.bg || 'transparent' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: heat?.fg || 'text.primary', whiteSpace: 'nowrap' }}>
                          {stat.format(val)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TeamsGrid — card view with WAR/CVR/DIFF badges (mirrors PlayersGrid)
   ═══════════════════════════════════════════════════════════════════════════ */

const TeamsGrid = ({ teams, activeCategory, isMobile, onTeamClick }) => {
  const theme = useTheme();

  const cardStats = activeCategory === 'batting'
    ? [
        { key: 'stats.batting.avg', label: 'AVG', format: fmtAvg },
        { key: 'stats.batting.ops', label: 'OPS', format: fmtAvg },
        { key: 'stats.batting.homeRuns', label: 'HR', format: fmtInt },
        { key: 'stats.batting.runs', label: 'R', format: fmtInt },
        { key: 'stats.batting.wOBA', label: 'wOBA', format: fmtAvg },
        { key: 'stats.batting.stolenBases', label: 'SB', format: fmtInt },
      ]
    : [
        { key: 'stats.pitching.era', label: 'ERA', format: fmtDec(2) },
        { key: 'stats.pitching.whip', label: 'WHIP', format: fmtDec(2) },
        { key: 'stats.pitching.fip', label: 'FIP', format: fmtDec(2) },
        { key: 'stats.pitching.strikeoutsPer9Inn', label: 'K/9', format: fmtDec(1) },
        { key: 'stats.pitching.saves', label: 'SV', format: fmtInt },
        { key: 'stats.pitching.qualityStart', label: 'QS', format: fmtInt },
      ];

  // Percentile-based color for each card stat within the pool
  const heatMaps = useMemo(() => {
    const out = {};
    for (const s of cardStats) out[s.key] = buildHeatMap(teams, s.key, INVERT_STATS.has(s.key));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, activeCategory]);

  return (
    <Grid container spacing={isMobile ? 1.5 : 2}>
      {teams.map((t, index) => {
        const teamColor = themeUtils.getTeamColor(t.id) || theme.palette.primary.main;
        const cvrDisplay = getCVRDisplay(t.cvr);
        const wins = t.record?.wins || 0;
        const losses = t.record?.losses || 0;

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
            <motion.div whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }}>
              <Card elevation={0} sx={{ height: '100%', cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`, borderTop: `3px solid ${teamColor}`,
                borderRadius: 3, overflow: 'hidden', transition: 'all 0.2s',
                '&:hover': { borderColor: alpha(teamColor, 0.5), boxShadow: `0 6px 20px ${alpha(teamColor, 0.12)}` } }}
                onClick={() => onTeamClick(t)}>
                <CardContent sx={{ p: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar src={teamLogo(t.id)} sx={{ width: 40, height: 40, bgcolor: 'white', mr: 1.5 }}
                      imgProps={{ style: { objectFit: 'contain' } }}>{t.id}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>{t.fullName}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                        <Chip label={`${wins}-${losses}`} size="small"
                          sx={{ height: 18, fontSize: '0.72rem', fontWeight: 700, bgcolor: alpha(teamColor, 0.1), color: teamColor }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                          {(t.division || '').replace('American League', 'AL').replace('National League', 'NL')}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.8rem', color: 'text.disabled', ml: 0.5 }}>
                      #{index + 1}
                    </Typography>
                  </Box>

                  {/* WAR + CVR + DIFF badges */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 2,
                      bgcolor: alpha('#FFD700', 0.08), border: `1px solid ${alpha('#FFD700', 0.2)}` }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>WAR</Typography>
                      <Typography variant="body2" fontWeight={800}
                        sx={{ fontSize: '0.95rem', color: t.warTotal >= 25 ? '#4CAF50' : t.warTotal >= 12 ? '#FFC107' : '#F44336' }}>
                        {t.warTotal.toFixed(1)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 2,
                      bgcolor: alpha('#FF6F00', 0.08), border: `1px solid ${alpha('#FF6F00', 0.2)}` }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>CVR</Typography>
                      <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.95rem', color: cvrDisplay.color }}>
                        {cvrDisplay.value}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 2,
                      bgcolor: alpha(teamColor, 0.06), border: `1px solid ${alpha(teamColor, 0.2)}` }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>DIFF</Typography>
                      <Typography variant="body2" fontWeight={800}
                        sx={{ fontSize: '0.95rem', color: t.runDiff >= 0 ? '#4CAF50' : '#F44336' }}>
                        {t.runDiff > 0 ? '+' : ''}{t.runDiff}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Key stats */}
                  <Grid container spacing={0.5}>
                    {cardStats.map(stat => {
                      const val = getNested(t, stat.key);
                      const heat = heatMaps[stat.key]?.[t.id];
                      return (
                        <Grid item xs={4} key={stat.key}>
                          <Box sx={{ textAlign: 'center', py: 0.4 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>
                              {stat.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}
                              sx={{ fontSize: '0.82rem', fontFamily: 'monospace', display: 'block',
                                color: (heat && heat.fg !== 'inherit') ? heat.fg : 'text.primary' }}>
                              {stat.format(val)}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        );
      })}
    </Grid>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Skeleton loader
   ═══════════════════════════════════════════════════════════════════════════ */

const TeamsSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
    <Skeleton variant="rectangular" height={44} sx={{ mb: 2, borderRadius: 3 }} />
    <Skeleton variant="rectangular" height={52} sx={{ mb: 2, borderRadius: 3 }} />
    <Skeleton variant="rectangular" height={520} sx={{ borderRadius: 2 }} />
  </Box>
);

export default Teams;
